import React from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, FileText, MapPin, Navigation } from "lucide-react";

/**
 * Tarjetas bajo una respuesta del asistente (las crea el back con mostrar_documento / mostrar_mapa, con datos del
 * alcance del usuario; el modelo no pone los enlaces):
 * - documento: PDF con "Abrir" (hoja de visita / identificación de riesgos en /doc-render, o archivo del storage:
 *   documentos de proveedores y PDF antiguos), como los adjuntos PDF del Chat de LegalIA.
 * - mapa: Google Maps con la comunidad y "Cómo llegar" (ruta desde la ubicación del usuario, en Google Maps).
 */
const abrirEnPestana = (url) => {
  const ventana = window.open(url, "_blank", "noopener,noreferrer");
  if (ventana) ventana.opener = null;
};

// Solo rutas internas de documentos o archivos del storage (nunca una URL que no hayamos construido nosotros)
const urlDocumento = (tarjeta) => {
  if (typeof tarjeta.ruta === "string" && /^\/doc-render\//.test(tarjeta.ruta)) return tarjeta.ruta;
  if (typeof tarjeta.archivo === "string" && tarjeta.archivo && !/^[a-z]+:/i.test(tarjeta.archivo)) {
    return `${process.env.REACT_APP_STORAGE_URL || ""}${tarjeta.archivo}`;
  }
  return null;
};

const formatoFecha = (iso) => {
  if (!iso) return null;
  const [a, m, d] = String(iso).slice(0, 10).split("-");
  return a && m && d ? `${d}/${m}/${a}` : null;
};

const COLOR_ESTADO = {
  vigente: "bg-emerald-50 text-emerald-700",
  caducado: "bg-red-50 text-red-700",
  "pendiente de validar": "bg-amber-50 text-amber-700",
};

function TarjetaDocumento({ tarjeta, t }) {
  const url = urlDocumento(tarjeta);
  const fecha = formatoFecha(tarjeta.fecha);
  const estado = tarjeta.estado ? t(`laeai.cards.status.${tarjeta.estado.replace(/ /g, "_")}`, { defaultValue: tarjeta.estado }) : null;
  // Hojas de visita / identificación: fecha de la visita; documentos de proveedor: caducidad
  const detalle = fecha ? (tarjeta.ruta || !tarjeta.estado ? t("laeai.cards.visitDate", { date: fecha }) : t("laeai.cards.expires", { date: fecha })) : null;
  return (
    <div className="flex w-full max-w-md flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600">
        <FileText size={20} />
      </div>
      <div className="min-w-[150px] flex-1">
        <p className="m-0 truncate text-sm font-medium text-gray-900" title={tarjeta.titulo}>{tarjeta.titulo}</p>
        <p className="m-0 flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
          <span>PDF</span>
          {/* Servicio y año del documento de la comunidad (p. ej. "CAE 2026"), como las pestañas de su ficha */}
          {typeof tarjeta.servicio === "string" && <span>· {tarjeta.servicio}</span>}
          {detalle && <span>· {detalle}</span>}
          {estado && (
            <span className={`rounded-full px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide ${COLOR_ESTADO[tarjeta.estado] || "bg-gray-100 text-gray-600"}`}>
              {estado}
            </span>
          )}
        </p>
      </div>
      <button
        type="button"
        disabled={!url}
        onClick={() => url && abrirEnPestana(url)}
        className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 disabled:cursor-default disabled:opacity-50"
      >
        <ExternalLink size={14} /> {t("laeai.cards.open")}
      </button>
    </div>
  );
}

function TarjetaMapa({ tarjeta, t, sinEnlacesInternos }) {
  const navigate = useNavigate();
  const conCoordenadas = Number.isFinite(tarjeta.lat) && Number.isFinite(tarjeta.lon);
  // Con coordenadas, el punto exacto; si no, Google busca la dirección
  const destino = conCoordenadas ? `${tarjeta.lat},${tarjeta.lon}` : tarjeta.direccion;
  if (!destino) return null;
  const mapa = `https://maps.google.com/maps?q=${encodeURIComponent(destino)}&z=16&output=embed`;
  const comoLlegar = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destino)}`;
  const enlaceComunidad = typeof tarjeta.enlace_comunidad === "string" ? tarjeta.enlace_comunidad.replace(/^https?:\/\/[^/]+/, "") : null;
  return (
    <div className="w-full max-w-md overflow-hidden rounded-xl border border-gray-200 bg-white">
      <iframe
        title={t("laeai.cards.mapTitle", { name: tarjeta.titulo })}
        src={mapa}
        loading="lazy"
        referrerPolicy="no-referrer"
        className="block h-48 w-full border-0"
      />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5">
        <MapPin size={18} className="shrink-0 text-red-500" />
        <div className="min-w-[150px] flex-1">
          {!sinEnlacesInternos && enlaceComunidad && enlaceComunidad.startsWith("/comunidades/") ? (
            <button
              type="button"
              onClick={() => navigate(enlaceComunidad)}
              className="m-0 block max-w-full cursor-pointer truncate border-none bg-transparent p-0 text-left text-sm font-medium text-gray-900 hover:underline"
              title={tarjeta.titulo}
            >
              {tarjeta.titulo}
            </button>
          ) : (
            <p className="m-0 truncate text-sm font-medium text-gray-900">{tarjeta.titulo}</p>
          )}
          <p className="m-0 truncate text-xs text-gray-500" title={tarjeta.direccion}>{tarjeta.direccion}</p>
        </div>
        <button
          type="button"
          onClick={() => abrirEnPestana(comoLlegar)}
          className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border-none bg-[#111] px-3 py-1.5 text-sm text-white hover:opacity-[0.88]"
        >
          <Navigation size={14} /> {t("laeai.cards.directions")}
        </button>
      </div>
    </div>
  );
}

export default function LAEAITarjetas({ tarjetas, t, sinEnlacesInternos = false }) {
  if (!Array.isArray(tarjetas) || !tarjetas.length) return null;
  return (
    <div className="mt-3 flex flex-col gap-2">
      {tarjetas.map((tarjeta, i) => {
        if (tarjeta?.tipo === "documento") return <TarjetaDocumento key={i} tarjeta={tarjeta} t={t} />;
        if (tarjeta?.tipo === "mapa") return <TarjetaMapa key={i} tarjeta={tarjeta} t={t} sinEnlacesInternos={sinEnlacesInternos} />;
        return null;
      })}
    </div>
  );
}
